import { __awaiter } from "tslib";
import { Choreographer } from '../Choreographer';
import { MessageDeliveryStatus } from '../constants';
/**
 * Abstract class to be used for subclassing individual choreographer plugin classes. Internally,
 * these can do whatever is needed for the message service being wrapped, so long as they adhere
 * to the IChoreographerPlugin contract for communicating with the parent choreographer API.
 */
export class ChoreographerPlugin {
    /**
     * Instantiates a plugin to be used with the main choreographer API. Requires a productId string
     * to be used for segmenting registered messageIds in other parts of the API, which ensures uniqueness
     * between individual products for tracking purposes.
     *
     * @param productId The name of the product for which this plugin will be managing communications with the choreographer API.
     * @returns A new instance of this plugin to use for integrating a 1P or 3P product with the choreographer API.
     */
    constructor(productId) {
        this._isDisabled = false;
        this._isDisplayingMessage = false;
        this._productId = productId;
        this._unregisterPlugin = this._registerPlugin();
    }
    /**
     * Public method to clean up this plugin instance, which will handle unregistering it from the choreographer API.
     */
    destroy() {
        this._unregisterPlugin();
    }
    /**
     * Determines whether this plugin is currently displaying a message on the screen. By default, is a simple boolean
     * toggle that gets flipped back and forth, but it could also be a function that checks the DOM for a given message
     * container to be present, or checks with some external API to tell it whether a message is being displayed, or
     * anything else. The logic is up to the consumer to implement, if something custom is desired.
     *
     * @returns A boolean that represents whether this plugin is currently displaying a message on the screen.
     */
    isDisplayingMessage() {
        return this._isDisplayingMessage;
    }
    /**
     * Disables the plugin from being able to issue startMessage requests. By default, this is a simple boolean toggle
     * that gets flipped back and forth, but it could also be a call to an external API to disable some functionality
     * via its own public interface. The logic is up to the consumer to implement, if something custom is desired.
     */
    disable() {
        this._isDisabled = true;
    }
    /**
     * Enables the plugin from being able to issue startMessage requests. By default, this is a simple boolean toggle
     * that gets flipped back and forth, but it could also be a call to an external API to disable some functionality
     * via its own public interface. The logic is up to the consumer to implement, if something custom is desired.
     */
    enable() {
        this._isDisabled = false;
    }
    /**
     * Makes a request to start a message through the choreographer API. Will immediately return false if this plugin
     * is currently in a disabled state, and will otherwise return the ultimate result of the startMessage request.
     *
     * @param messageId The messageId for the message to be started through the choreographer API.
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message start operation.
     */
    startMessage(messageId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._isDisabled) {
                return MessageDeliveryStatus.BLOCKED;
            }
            const didStart = yield Choreographer.getInstance().startMessage(this._productId, messageId, this);
            if (didStart) {
                this._isDisplayingMessage = true;
            }
            return didStart;
        });
    }
    /**
     * Makes a request to stop a message through the choreographer API. Will return the ultimate result of the stopMessage request.
     *
     * @param messageId The messageId for the message to be stopped through the choreographer API.
     * @returns A Promise that resolves to a MessageDeliveryStatus value, to denote the ultimate status of the attempted message stop operation.
     */
    stopMessage(messageId) {
        return __awaiter(this, void 0, void 0, function* () {
            const didStop = yield Choreographer.getInstance().stopMessage(this._productId, messageId);
            if (didStop) {
                this._isDisplayingMessage = false;
            }
            return didStop;
        });
    }
    /**
     * Subscribes callbacks to be used for starting and stopping message requests through the choreographer, keyed by a unique (to this plugin instance) messageId.
     *
     * @param messageId Unique identifier within the context of all of this plugin's message entries, to be used as a key for this message subscription.
     * @param options An object containing start and stop callback functions, to be invoked upon successful requests to startMessage and stopMessage, respectively.
     */
    on(messageId, options) {
        return Choreographer.getInstance().on(this._productId, messageId, options);
    }
    /**
     * Subscribes a callback to be used for starting message requests through the choreographer, keyed by a unique (to this plugin instance) messageId.
     *
     * @param messageId Unique identifier within the context of all of this plugin's message entries, to be used as a key for this message subscription.
     * @param startCallback A callback to be invoked upon successful requests to startMessage.
     */
    onStart(messageId, startCallback) {
        return Choreographer.getInstance().onStart(this._productId, messageId, startCallback);
    }
    /**
     * Subscribes a callback to be used for stopping message requests through the choreographer, keyed by a unique (to this plugin instance) messageId.
     *
     * @param messageId Unique identifier within the context of all of this plugin's message entries, to be used as a key for this message subscription.
     * @param stopCallback A callback to be invoked upon successful requests to stopMessage.
     */
    onStop(messageId, stopCallback) {
        return Choreographer.getInstance().onStop(this._productId, messageId, stopCallback);
    }
    /**
     * Unsubscribes a message's start and stop message callbacks from the choreographer, keyed by a unique (to this plugin instance) messageId.
     *
     * @param messageId Unique identifier within the context of all of this plugin's message entries, to be used as a key for unsubscribing this message subscription.
     */
    off(messageId) {
        return Choreographer.getInstance().off(this._productId, messageId);
    }
    /**
     * Registers this plugin instance with the choreographer API. Allows the choreographer to enable and disable it as needed to ensure individual message experiences on screen.
     *
     * @returns A callback to be used to unregister this plugin with the choreographer instance, for cleanup purposes, if necessary.
     */
    _registerPlugin() {
        this._unregisterPlugin = Choreographer.getInstance().registerPlugin(this._productId, this);
        return this._unregisterPlugin;
    }
}
//# sourceMappingURL=types.js.map